import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAppSelector } from '@/hooks/useAppSelector';
import { Filter, ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CompanyNavigationProps {
  onBack: () => void;
  onPrevious: () => void;
  onNext: () => void;
  disableNavigation: boolean;
}

const CompanyNavigation: React.FC<CompanyNavigationProps> = ({
  onBack,
  onPrevious,
  onNext,
  disableNavigation
}) => {
  const navigate = useNavigate();
  
  // Check if any filters are active
  const selectedCategories = useAppSelector(state => state.companiesFilter.selectedCategories) || [];
  const selectedEmployees = useAppSelector(state => state.companiesFilter.selectedEmployees) || [];
  const selectedProductCount = useAppSelector(state => state.companiesFilter.selectedProductCount) || [];
  const selectedModalities = useAppSelector(state => state.companiesFilter.selectedModalities) || [];
  
  const hasActiveFilters = 
    (selectedCategories.length > 0 && !selectedCategories.includes('All')) ||
    (selectedEmployees.length > 0 && !selectedEmployees.includes('All')) ||
    (selectedProductCount.length > 0 && !selectedProductCount.includes('All')) ||
    (selectedModalities.length > 0 && !selectedModalities.includes('All'));
  
  // Function to navigate back to companies page with filters preserved
  const navigateToFilteredResults = () => {
    // Build query parameters for filters
    const queryParams = new URLSearchParams();
    
    // Only add non-default filter values
    if (selectedCategories.length > 0 && !selectedCategories.includes('All')) {
      queryParams.set('categories', selectedCategories.join(','));
    }
    if (selectedEmployees.length > 0 && !selectedEmployees.includes('All')) {
      queryParams.set('employees', selectedEmployees.join(','));
    }
    if (selectedProductCount.length > 0 && !selectedProductCount.includes('All')) {
      queryParams.set('productCount', selectedProductCount.join(','));
    }
    if (selectedModalities.length > 0 && !selectedModalities.includes('All')) {
      queryParams.set('modalities', selectedModalities.join(','));
    }
    
    // Navigate to companies page with filter parameters
    const queryString = queryParams.toString();
    navigate(`/companies${queryString ? `?${queryString}` : ''}`);
  };
  
  return (
    <div className="flex justify-between items-center mb-6">
      <div className="flex space-x-2">
        {hasActiveFilters ? (
          <Button 
            variant="outline" 
            size="sm"
            onClick={navigateToFilteredResults}
            title="Press Esc to go back to filtered results"
            className="transition-all duration-200 hover:bg-gray-100 flex items-center gap-1"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Filtered Results</span>
            <span className="inline sm:hidden">Filtered Results</span>
          </Button>
        ) : (
          <Button 
            variant="outline" 
            size="sm"
            onClick={onBack}
            title="Press Esc to go back"
            className="transition-all duration-200 hover:bg-gray-100"
          >
            Back to Companies
          </Button>
        )}
      </div>
      
      <div className="flex items-center">
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onPrevious}
            title="Press ← to go to previous company"
            disabled={disableNavigation}
            className="transition-all duration-200 hover:bg-gray-100 flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs bg-blue-50 text-blue-700">
                Filtered
              </Badge>
            )}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={onNext}
            title="Press → to go to next company"
            disabled={disableNavigation}
            className="transition-all duration-200 hover:bg-gray-100 flex items-center gap-1"
          >
            Next
            <ArrowRight className="h-4 w-4" />
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs bg-blue-50 text-blue-700">
                Filtered
              </Badge>
            )}
          </Button>
        </div>
        <div className="text-xs text-gray-400 ml-2 hidden sm:block">
          Tip: Use arrow keys ← → to navigate
        </div>
      </div>
    </div>
  );
};

export default CompanyNavigation;
